
import { Component, Emit, Prop, VModel, Vue, Watch } from 'vue-property-decorator';
import {
  AiChatMessageDto,
  AiChatRoleEnum,
  SupportTicketDto,
  SupportTicketMessageDto,
  SupportTicketsClient,
  SupportTicketStatusEnum,
} from '@/services/api-clients';
import SupportTicketMessageBubble from '@/components/SupportTicketMessageBubble.vue';
import SupportTicketSystemMessageBubble from '@/components/SupportTicketSystemMessageBubble.vue';
import SupportTicketMessageInput from '@/components/SupportTicketMessageInput.vue';
import SupportTicketTypingIndicator from '@/components/SupportTicketTypingIndicator.vue';
import { RouteHelper } from '@/router/route.helper';
import { isScrolledNearBottom } from '@/common/Utils.common';
import { driverChatHub } from '@/services/driver-chat-hub';
import { aiMessageContentFilter } from '@/filters';

@Component({
  components: {
    SupportTicketMessageBubble,
    SupportTicketSystemMessageBubble,
    SupportTicketMessageInput,
    SupportTicketTypingIndicator,
  },
})
export default class SupportChatPopup extends Vue {
  @VModel({ type: Boolean, required: true, default: false }) popupOpen!: boolean;

  @Prop({ type: Object, required: true }) ticket!: SupportTicketDto;

  @Prop({ type: Array, required: true }) messages!: SupportTicketMessageDto[];

  @Prop({ type: String, required: false, default: null }) deletedByName!: string | null;

  SupportTicketStatusEnum = SupportTicketStatusEnum;

  AiChatRoleEnum = AiChatRoleEnum;

  RouteHelper = RouteHelper;

  aiMessageContent = aiMessageContentFilter;

  loading = false;

  isLoadingOlder = false;

  hasMoreOlder = false;

  aiChatHistory: AiChatMessageDto[] = [];

  typingName: string | null = null;

  permissionSaving = false;

  private readonly pageSize = 50;

  private unsubscribeTyping: (() => void) | null = null;

  private typingHideTimeout: number | null = null;

  private lastTypingSentAt = 0;

  get cardStyle(): Record<string, string> {
    if (this.$vuetify.breakpoint.smAndDown) return { height: '100vh' };
    return { height: '520px', width: '400px' };
  }

  get isInputDisabled(): boolean {
    return this.ticket.statusId === SupportTicketStatusEnum.Closed
      || !!this.deletedByName;
  }

  get initials(): string {
    const parts = (this.ticket.driverFullName ?? '').split(' ').filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }

  @Watch('popupOpen', { immediate: true })
  onPopupOpenChanged(value: boolean) {
    if (value) {
      this.loadDetails();
      this.scrollToBottomAfterRender();
    }
  }

  // Reload when switching between conversations while the popup stays open
  // (e.g. employee clicks another chathead — dialog is persistent and only the ticket prop changes).
  @Watch('ticket')
  onTicketChanged(next: SupportTicketDto, previous: SupportTicketDto) {
    if (!this.popupOpen) return;
    if (next?.id === previous?.id) return;
    this.aiChatHistory = [];
    this.hasMoreOlder = false;
    this.typingName = null;
    this.loadDetails();
    this.scrollToBottomAfterRender();
  }

  @Watch('messages')
  onMessagesChanged() {
    // Messages arrive as a prop, so this watcher is the only place a new message is observed here.
    this.typingName = null;
    this.scrollToBottomAfterRender();
  }

  mounted() {
    this.unsubscribeTyping = driverChatHub.onTypingStarted((id, name) => this.onTypingStarted(id, name));
  }

  beforeDestroy() {
    if (this.unsubscribeTyping) this.unsubscribeTyping();
    if (this.typingHideTimeout) window.clearTimeout(this.typingHideTimeout);
  }

  onTyping() {
    if (this.ticket.id == null) return;
    const now = Date.now();
    if (now - this.lastTypingSentAt < 2000) return;
    this.lastTypingSentAt = now;
    driverChatHub.notifyTyping(this.ticket.id).catch(() => undefined);
  }

  onTypingStarted(ticketId: number, firstName: string) {
    if (ticketId !== this.ticket.id) return;
    // Measure before the indicator renders, and only follow when the reader is already at the
    // bottom. Someone scrolled up reading history must not be pulled down by the dots appearing.
    const shouldFollow = this.typingName === null && this.isNearBottom();
    this.typingName = firstName;
    if (shouldFollow) this.scrollToBottomAfterRender();
    if (this.typingHideTimeout) window.clearTimeout(this.typingHideTimeout);
    this.typingHideTimeout = window.setTimeout(() => {
      this.typingName = null;
    }, 4000);
  }


  loadDetails() {
    if (this.ticket.id == null) return;
    this.loading = true;
    new SupportTicketsClient().getTicketDetails(this.ticket.id).then(({ success, result, errors }) => {
      if (success && result) {
        this.aiChatHistory = result.aiChatHistory ?? [];
        const loadedMessages = result.messages ?? [];
        this.hasMoreOlder = loadedMessages.length >= this.pageSize;
        this.emitMessagesLoaded(loadedMessages);
      } else {
        this.$notifier.error(errors ?? []);
      }
    }).finally(() => {
      this.loading = false;
      this.scrollToBottomAfterRender();
    });
  }

  loadOlder() {
    if (this.isLoadingOlder || !this.hasMoreOlder || this.ticket.id == null) return;
    this.isLoadingOlder = true;
    const container = this.$refs.messagesContainer as HTMLElement | undefined;
    const prevScrollHeight = container?.scrollHeight ?? 0;
    const prevScrollTop = container?.scrollTop ?? 0;

    new SupportTicketsClient().getTicketMessages(this.ticket.id, this.messages.length, this.pageSize).then(({ success, result, errors }) => {
      if (success && result) {
        this.hasMoreOlder = result.length >= this.pageSize;
        this.emitOlderLoaded(result);
        this.$nextTick(() => {
          if (container) {
            container.scrollTop = container.scrollHeight - prevScrollHeight + prevScrollTop;
          }
        });
      } else {
        this.$notifier.error(errors ?? []);
      }
    }).finally(() => {
      this.isLoadingOlder = false;
    });
  }

  @Emit('older-loaded')
  emitOlderLoaded(messages: SupportTicketMessageDto[]): SupportTicketMessageDto[] {
    return messages;
  }

  scrollToBottom() {
    const container = this.$refs.messagesContainer as HTMLElement | undefined;
    if (container) container.scrollTop = container.scrollHeight;
  }

  private isNearBottom(): boolean {
    const container = this.$refs.messagesContainer as HTMLElement | undefined;
    return container ? isScrolledNearBottom(container) : false;
  }

  private scrollToBottomAfterRender() {
    this.$nextTick(() => {
      requestAnimationFrame(() => this.scrollToBottom());
    });
  }

  @Emit('messages-loaded')
  emitMessagesLoaded(messages: SupportTicketMessageDto[]): SupportTicketMessageDto[] {
    return messages;
  }

  @Emit('send')
  emitSend(content: string): { ticketId: number; content: string } {
    return { ticketId: this.ticket.id as number, content };
  }

  onSendInput(content: string) {
    if (this.ticket.id == null) return;
    this.emitSend(content);
  }

  grantUploadPermission(maxFiles: number) {
    if (this.ticket.id == null) return;
    this.permissionSaving = true;
    new SupportTicketsClient().grantUploadPermission({ ticketId: this.ticket.id, maxFiles }).then(({ success, errors }) => {
      if (!success) {
        this.$notifier.error(errors ?? []);
      }
    }).finally(() => {
      this.permissionSaving = false;
    });
  }

  endUploadPermission() {
    if (this.ticket.id == null) return;
    this.permissionSaving = true;
    new SupportTicketsClient().endUploadPermission({ ticketId: this.ticket.id }).then(({ success, errors }) => {
      if (!success) {
        this.$notifier.error(errors ?? []);
      }
    }).finally(() => {
      this.permissionSaving = false;
    });
  }

  openFullView() {
    this.popupOpen = false;
  }

  close() {
    this.popupOpen = false;
  }
}
